import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from "./infoicon.module.css";
import defaultIcon from "./info.svg";

const InfoIcon = ({
  content,
  placement = "top", //top, bottom, left, right 
  children,
  iconSrc,
  size = 15,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });
  
  // New State: We might render in a different place than the prop requested
  const [activePlacement, setActivePlacement] = useState(placement);

  const timeoutRef = useRef(null);
  const iconRef = useRef(null);

  // Configuration
  const GAP = 12; // Space between icon and tooltip
  const ESTIMATED_SIZE = 180; // Estimated px size of tooltip for boundary checks

  const handleShow = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Recalculate position immediately before showing
    updatePosition();
    setIsVisible(true);
  };

  const handleHide = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 200);
  };

  const updatePosition = () => {
    if (!iconRef.current) return;
    
    const rect = iconRef.current.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;

    // 1. SMART FLIP LOGIC
    // Check if the preferred placement fits. If not, flip to opposite.
    let finalPlacement = placement;

    if (placement === "top" && rect.top < ESTIMATED_SIZE) {
      finalPlacement = "bottom";
    } else if (placement === "bottom" && (innerHeight - rect.bottom) < ESTIMATED_SIZE) {
      finalPlacement = "top";
    } else if (placement === "left" && rect.left < ESTIMATED_SIZE) {
      finalPlacement = "right";
    } else if (placement === "right" && (innerWidth - rect.right) < ESTIMATED_SIZE) {
      finalPlacement = "left";
    }

    setActivePlacement(finalPlacement);

    // 2. COORDINATE CALCULATION (Based on the DECIDED placement, not the prop)
    let newCoords = { left: 0, top: 0 };

    switch (finalPlacement) {
      case "top":
        newCoords = {
          left: rect.left + rect.width / 2,
          top: rect.top - GAP,
        };
        break;
      case "bottom":
        newCoords = {
          left: rect.left + rect.width / 2,
          top: rect.bottom + GAP,
        };
        break;
      case "left":
        newCoords = {
          left: rect.left - GAP,
          top: rect.top + rect.height / 2,
        };
        break;
      case "right":
        newCoords = {
          left: rect.right + GAP,
          top: rect.top + rect.height / 2,
        };
        break;
      default:
        newCoords = { left: rect.left, top: rect.bottom };
    }

    setCoords(newCoords);
  };

  // Listen for scroll/resize to update position dynamically if open
  useEffect(() => {
    if (isVisible) {
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);
    }
    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, placement]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        className={styles.triggerButton}
        onMouseEnter={handleShow}
        onMouseLeave={handleHide}
        onFocus={handleShow}
        onBlur={handleHide}
        onClick={handleShow}
        aria-expanded={isVisible}
      >
        {children ? (
          children
        ) : (
          <img
            src={defaultIcon}
            alt="More information"
            width={size}
            height={size}
            className={styles.iconImage}
          />
        )}
      </button>

      {isVisible &&
        ReactDOM.createPortal(
          <div
            className={styles.tooltip}
            // IMPORTANT: Pass the CALCULATED placement to CSS
            data-placement={activePlacement} 
            style={{
              left: coords.left,
              top: coords.top,
            }}
            onMouseEnter={handleShow}
            onMouseLeave={handleHide}
            role="tooltip"
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};


export default React.memo(InfoIcon);;